import { useState, useCallback, useRef } from 'react';
import { preprocessContent } from '../../AIChatbotPlugin/components/MessageContent/contentPreprocessor';
import { useSvgStore } from '../store/svgStore';

/** 检测到的 SVG 信息 */
export interface DetectedSvgInfo {
  /** SVG 原始内容 */
  readonly svgContent: string;
  /** 触发该 SVG 的用户 prompt（如果能追踪到） */
  readonly prompt: string;
  /** 检测时间戳 */
  readonly detectedAt: number;
}

/**
 * 清洗 SVG 内容，确保是纯净的 SVG 字符串
 * - 去除 markdown 代码块残留
 * - 去除前后空白
 * - 确保以 <svg 开头，以 </svg> 结尾
 */
const sanitizeSvgContent = (raw: string): string | null => {
  let content = raw.trim();

  // 去除可能残留的 markdown 代码块标记
  content = content.replace(/^```(?:svg|xml|html)?\s*\n?/i, '');
  content = content.replace(/\n?```\s*$/i, '');
  content = content.trim();

  // 提取 <svg...>...</svg> 部分（可能前后有额外文本）
  const svgMatch = content.match(/<svg[\s\S]*<\/svg>/i);
  if (!svgMatch) {
    return null; // 不是有效的 SVG
  }

  return svgMatch[0];
};

/** Hook 返回值 */
export interface UseSvgFromAIReturn {
  /** 当前检测到的 SVG（null 表示没有待处理的） */
  readonly detectedSvg: DetectedSvgInfo | null;
  /** 处理 AI 消息回调（传给 AiChatPage.onMessageReceived） */
  readonly handleAIMessage: (rawMessage: string) => void;
  /** 添加到画布 */
  readonly applyToCanvas: () => void;
  /** 保存到素材库 */
  readonly saveToLibrary: () => Promise<void>;
  /** 导出为 SVG 文件 */
  readonly exportSvgFile: () => void;
  /** 关闭/忽略检测提示 */
  readonly dismiss: () => void;
  /** 记录用户 prompt（传给 AiChatPage.onBeforeSend） */
  readonly trackPrompt: (text: string) => string;
}

/**
 * 从 AI 消息中解析 SVG 并提供操作方法
 *
 * 防抖策略：流式传输中 onMessageReceived 会被高频调用，
 * 用 debounce + 内容去重 避免无限渲染和重复写入历史。
 */
export const useSvgFromAI = (): UseSvgFromAIReturn => {
  const [detectedSvg, setDetectedSvg] = useState<DetectedSvgInfo | null>(null);

  // ─── Refs（避免闭包过期 & 去重） ─────────────────────
  const lastPromptRef = useRef<string>('');
  /** 上一次已处理的 SVG 内容（用于去重，避免流式 chunk 重复触发） */
  const lastProcessedSvgRef = useRef<string>('');
  /** 是否正在执行操作（添加画布/保存等），期间屏蔽新的检测更新 */
  const isOperatingRef = useRef<boolean>(false);
  /** 最新检测到的 SVG（ref 版本，供操作回调读取，避免闭包过期） */
  const detectedSvgRef = useRef<DetectedSvgInfo | null>(null);
  /** 防抖 timer */
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** addAiHistory 去重：已记录过的 SVG 内容 hash */
  const historyRecordedSetRef = useRef<Set<string>>(new Set());

  // ─── Store actions ─────────────────────
  const canvas = useSvgStore((s) => s.canvas);
  const addAiSvgToCanvas = useSvgStore((s) => s.addAiSvgToCanvas);
  const addAsset = useSvgStore((s) => s.addAsset);
  const addAiHistory = useSvgStore((s) => s.addAiHistory);

  /**
   * 内部：真正执行 SVG 检测更新的函数（防抖后调用）
   */
  const commitDetectedSvg = useCallback((svgContent: string, prompt: string): void => {
    // 正在执行操作时，不更新检测状态
    if (isOperatingRef.current) return;

    const info: DetectedSvgInfo = {
      svgContent,
      prompt,
      detectedAt: Date.now(),
    };

    detectedSvgRef.current = info;
    setDetectedSvg(info);

    // addAiHistory 去重：同一段 SVG 只记录一次
    const svgHash = svgContent.slice(0, 200); // 用前 200 字符作简易 hash
    if (!historyRecordedSetRef.current.has(svgHash)) {
      historyRecordedSetRef.current.add(svgHash);
      void addAiHistory({
        prompt,
        generated_svg: svgContent,
      });
    }
  }, [addAiHistory]);

  /**
   * 处理 AI 消息：解析内容，提取 SVG
   * 
   * ★ 带防抖 + 内容去重：
   *   - 流式传输中 onMessageReceived 每个 chunk 都会调用
   *   - 只有 SVG 内容真正变化时才更新状态
   *   - 300ms 防抖避免高频渲染
   */
  const handleAIMessage = useCallback((rawMessage: string): void => {
    try {
      const segments = preprocessContent(rawMessage);
      const svgSegment = segments.find((seg) => seg.type === 'svg');

      if (!svgSegment) return;

      // ★ 清洗 SVG 内容
      const cleanedSvg = sanitizeSvgContent(svgSegment.content);
      if (!cleanedSvg) return;

      // 内容去重：和上次相同则跳过
      if (svgSegment.content === lastProcessedSvgRef.current) return;
      lastProcessedSvgRef.current = svgSegment.content;

      // 防抖：取消上一次的 pending 更新
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      const capturedContent = cleanedSvg;
      const capturedPrompt = lastPromptRef.current;

      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;

        if (isOperatingRef.current) return;

        const info: DetectedSvgInfo = {
          svgContent: capturedContent,
          prompt: capturedPrompt,
          detectedAt: Date.now(),
        };

        detectedSvgRef.current = info;
        setDetectedSvg(info);

        const svgHash = capturedContent.slice(0, 200);
        if (!historyRecordedSetRef.current.has(svgHash)) {
          historyRecordedSetRef.current.add(svgHash);
          const { addAiHistory } = useSvgStore.getState();
          void addAiHistory({
            prompt: capturedPrompt,
            generated_svg: capturedContent,
          });
        }
      }, 300);
    } catch (error) {
      console.error('[useSvgFromAI] 解析 AI 消息失败:', error);
    }
  }, []);

  /** 添加到画布 */
  const applyToCanvas = useCallback((): void => {
    // 从 ref 读取最新值，避免闭包过期
    const currentSvg = detectedSvgRef.current;
    if (!currentSvg) return;

    // 加锁：防止操作期间 handleAIMessage 又更新状态
    isOperatingRef.current = true;

    try {
      const canvasWidth = canvas?.width ?? 1200;
      const canvasHeight = canvas?.height ?? 800;

      addAiSvgToCanvas(currentSvg.svgContent, 'AI 生成图层', {
        x: canvasWidth / 2,
        y: canvasHeight / 2,
      });

      // 清除检测状态
      detectedSvgRef.current = null;
      lastProcessedSvgRef.current = '';
      setDetectedSvg(null);
    } finally {
      // 延迟解锁，给渲染一个周期的缓冲
      setTimeout(() => {
        isOperatingRef.current = false;
      }, 100);
    }
  }, [canvas, addAiSvgToCanvas]);

  /** 保存到素材库 */
  const saveToLibrary = useCallback(async (): Promise<void> => {
    const currentSvg = detectedSvgRef.current;
    if (!currentSvg) return;

    isOperatingRef.current = true;

    try {
      await addAsset({
        name: currentSvg.prompt
          ? `AI: ${currentSvg.prompt.slice(0, 30)}`
          : `AI 生成 ${new Date().toLocaleString()}`,
        svg_code: currentSvg.svgContent,
        is_public: false,
        from_ai_prompt: currentSvg.prompt,
      });

      detectedSvgRef.current = null;
      lastProcessedSvgRef.current = '';
      setDetectedSvg(null);
    } catch (error) {
      console.error('[useSvgFromAI] 保存素材失败:', error);
    } finally {
      setTimeout(() => {
        isOperatingRef.current = false;
      }, 100);
    }
  }, [addAsset]);

  /** 导出为 SVG 文件 */
  const exportSvgFile = useCallback((): void => {
    const currentSvg = detectedSvgRef.current;
    if (!currentSvg) return;

    const blob = new Blob([currentSvg.svgContent], {
      type: 'image/svg+xml;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ai-hmi-${Date.now()}.svg`;
    link.click();
    URL.revokeObjectURL(url);
  }, []);

  /** 关闭检测提示 */
  const dismiss = useCallback((): void => {
    isOperatingRef.current = true;
    detectedSvgRef.current = null;
    lastProcessedSvgRef.current = '';
    setDetectedSvg(null);

    setTimeout(() => {
      isOperatingRef.current = false;
    }, 100);
  }, []);

  /** 记录用户 prompt */
  const trackPrompt = useCallback((text: string): string => {
    lastPromptRef.current = text;
    // 新一轮对话开始，重置去重状态
    lastProcessedSvgRef.current = '';
    historyRecordedSetRef.current.clear();
    return text;
  }, []);

  return {
    detectedSvg,
    handleAIMessage,
    applyToCanvas,
    saveToLibrary,
    exportSvgFile,
    dismiss,
    trackPrompt,
  };
};